import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../../design/Icon';
import { fmt } from '../../../design/format';

// Ported from docs/design/mockups/_build/shell-charts.js (plain SVG, no chart library).

export function nice(max, ticks = 4, integer = false) {
  if (max <= 0) return { max: 1, step: 1 };
  const raw = max / ticks;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const f = raw / mag;
  const allowQuarter = !integer || Number.isInteger(2.5 * mag);
  let step = (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 && allowQuarter ? 2.5 : f <= 5 ? 5 : 10) * mag;
  if (integer) step = Math.max(1, Math.round(step));
  return { max: Math.ceil(max / step) * step, step };
}

export const allIntegers = (values) => values.every((v) => v == null || Number.isInteger(v));

export const series = (i) => `var(--series-${i})`;

/** Sequential fill for t in 0–1. With { contrast: true } the ink is black or white, whichever meets WCAG 4.5:1 on that step. */
export const seqColor = (t, { contrast = false } = {}) => {
  const i = Math.max(0, Math.min(6, Math.round(t * 6)));
  const ink = contrast ? `var(--seq-ink-${i})` : i >= 4 ? 'var(--seq-text-dark)' : 'var(--seq-text-light)';
  return { fill: `var(--seq-${i})`, ink, i };
};

export function useChartWidth(minWidth = 280) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const w = Math.round(entries[0].contentRect.width);
      setWidth((current) => (w && Math.abs(w - current) > 2 ? w : current));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, Math.max(minWidth, width)];
}

/* ---- tooltip ---- */

export function useChartTip() {
  const [tip, setTip] = useState(null);
  const show = useCallback((content, x, y) => setTip({ content, x, y }), []);
  const hide = useCallback(() => setTip(null), []);
  useEffect(() => {
    if (!tip) return undefined;
    document.addEventListener('scroll', hide, true);
    return () => document.removeEventListener('scroll', hide, true);
  }, [tip, hide]);
  const node = tip ? <ChartTip {...tip} /> : null;
  return { node, show, hide };
}

function ChartTip({ content, x, y }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x + 14, top: y + 14 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let left = x + 14;
    let top = y + 14;
    if (left + w > window.innerWidth - 8) left = x - w - 14;
    if (top + h > window.innerHeight - 8) top = y - h - 14;
    setPos({ left: Math.max(8, left), top: Math.max(8, top) });
  }, [x, y, content]);
  return createPortal(
    <div ref={ref} className="chart-tip" role="tooltip" style={pos}>
      {content}
    </div>,
    document.body
  );
}

export function TipTitle({ children }) {
  return <div className="tt">{children}</div>;
}

export function TipRow({ color, label, value, style }) {
  return (
    <div className="tr" style={style}>
      {color !== undefined && <span className="sw" style={{ background: color }} />}
      <span>{label}</span>
      <span className="v">{value}</span>
    </div>
  );
}

/* ---- legend ---- */

export function ChartLegend({ items }) {
  return (
    <div className="legend" style={{ marginBottom: 10 }}>
      {items.map((s) => (
        <span key={s.name}>
          <i
            className={s.line ? 'line' : ''}
            style={{ background: s.dashed ? `repeating-linear-gradient(90deg,${s.color} 0 4px,transparent 4px 7px)` : s.color }}
          />
          {s.name}
        </span>
      ))}
    </div>
  );
}

/* ---- table twin ---- */

export function ChartTable({ head, rows }) {
  return (
    <div className="chart-table-wrap table-wrap">
      <table className="table compact chart-table">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={`${h}-${i}`} className={i ? 'n' : undefined}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, i) => (
                <td key={i} className={i ? 'n' : undefined}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Card-head "Table" toggle (mockup VL.tableBtn). Pass `on` to the chart's showTable prop. */
export function useTableToggle() {
  const [on, setOn] = useState(false);
  const button = (
    <button type="button" className="btn btn-ghost btn-sm" aria-pressed={on ? 'true' : 'false'} title="Show as table" onClick={() => setOn((v) => !v)}>
      <Icon name="table" className="ico-sm" />
      Table
    </button>
  );
  return { on, setOn, button };
}

export const defaultFmt = { k: fmt.k, int: fmt.int };
